import { useState } from 'react';
import { X } from 'lucide-react';
import { MAX_TAGS, normalizeTag, parseTags } from '../../lib/tags';

// Tag chips. Enter, comma or leaving the field turns the typed text into a
// chip; Backspace on an empty field removes the last one.
//
// `value` is the comma-separated string the parents and drafts already use, so
// adopting this component changed no draft format.
//
// Committing on blur matters: tapping "Post" blurs this field first, so a tag
// typed but never followed by a comma still makes it into the post.
const TagInput = ({ value, onChange, className = '' }) => {
  const tags = parseTags(value);
  const [pending, setPending] = useState('');
  const full = tags.length >= MAX_TAGS;

  const commit = (text) => {
    const next = parseTags([...tags, ...text.split(',')]);
    onChange(next.join(', '));
    setPending('');
  };

  const remove = (tag) => onChange(tags.filter(t => t !== tag).join(', '));

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ',') && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (pending.trim()) commit(pending);
    } else if (e.key === 'Backspace' && !pending && tags.length) {
      remove(tags[tags.length - 1]);
    }
  };

  const handleChange = (e) => {
    const text = e.target.value;
    // A pasted "a, b, c" becomes three chips at once.
    if (text.includes(',')) commit(text);
    else setPending(text);
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-1.5 px-3 py-2 rounded-xl ${className}`}
      style={{ backgroundColor: 'var(--input-bg)', border: '1px solid var(--input-border)' }}
    >
      {tags.map(tag => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-xs"
          style={{ backgroundColor: 'var(--pill-bg)', border: '1px solid var(--pill-border)', color: 'var(--text-secondary)' }}
        >
          #{tag}
          <button
            type="button"
            onClick={() => remove(tag)}
            aria-label={`Remove tag ${tag}`}
            className="p-0.5 rounded-full hover-surface"
          >
            <X size={12} />
          </button>
        </span>
      ))}
      {!full && (
        <input
          type="text"
          value={pending}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={() => { if (normalizeTag(pending)) commit(pending); else setPending(''); }}
          placeholder={tags.length ? 'Add another' : `Tags — press Enter after each (up to ${MAX_TAGS})`}
          aria-label="Add a tag"
          className="flex-1 min-w-[8rem] py-1 bg-transparent text-sm text-primary placeholder:text-tertiary focus:outline-none"
        />
      )}
      {full && (
        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>{MAX_TAGS} tags max</span>
      )}
    </div>
  );
};

export default TagInput;
